/**
 * Estoque (F3b) — porte de src/pages/Estoque do app atual, mesmas regras
 * (packages/core/services/estoqueService): saldo = soma do kardex (nunca editado),
 * ajuste de inventário com observação obrigatória, unidades com série, origem
 * por importação, reordenar por arrastar e extrato em PDF (o MESMO gerador do
 * app atual, src/lib/pdf.ts, importado sem cópia).
 */
import { createEffect, createMemo, createResource, createSignal, For, Index, on, Show } from 'solid-js'
import { Button } from '@ui/components/Button'
import { Input } from '@ui/components/Input'
import { Tabs } from '@ui/components/Tabs'
import type { ModeloLista, TipoMovimento, UnidadeEstoqueDTO } from '@shared/types'
import { gerarExtratoEstoquePDF } from '../../../../src/lib/pdf'
import { api, versaoDados } from '../nucleo/cliente'
import { mensagemErro } from '../lib/erro'
import { AreaTexto, brl, Campo, criarRecarga, Erro, Etiqueta, Formulario, Marcar, num, Selecao, T, Vazio } from '../ui/kit'

// ---- ordem dos saldos: IGUAL ao app atual (src/pages/Estoque/EstoquePage.tsx)
const ORDEM_LINHAS = ['vent', 'flex', 'flap', 'hrd', 'sensor', 'spare']
const collator = new Intl.Collator('pt-BR', { numeric: true, sensitivity: 'base' })
function prioridadeLinha(texto: string): number {
  const t = texto.toLowerCase()
  for (let i = 0; i < ORDEM_LINHAS.length; i++) if (t.includes(ORDEM_LINHAS[i])) return i
  return ORDEM_LINHAS.length
}
function ordenarPorLinha(rows: ModeloLista[]): ModeloLista[] {
  const grupos = new Map<string, ModeloLista[]>()
  for (const m of rows) {
    const arr = grupos.get(m.linhaNome) ?? []
    arr.push(m)
    grupos.set(m.linhaNome, arr)
  }
  const linhas = [...grupos.entries()].map(([nome, membros]) => ({
    membros,
    prioridade: prioridadeLinha(`${nome} ${membros.map((x) => `${x.produtoNome} ${x.codigoModelo}`).join(' ')}`),
    count: membros.length,
    nome,
  }))
  linhas.sort((a, b) => (a.prioridade !== b.prioridade ? a.prioridade - b.prioridade : a.count !== b.count ? b.count - a.count : a.nome.localeCompare(b.nome)))
  return linhas.flatMap((g) => g.membros.slice().sort((x, y) => collator.compare(x.codigoModelo, y.codigoModelo)))
}
/** Primeiro os posicionados à mão (na ordem salva), depois os demais pela ordem padrão. */
function ordenarSaldos(rows: ModeloLista[]): ModeloLista[] {
  const posicionados = rows.filter((m) => m.posicao != null).sort((a, b) => (a.posicao as number) - (b.posicao as number))
  return [...posicionados, ...ordenarPorLinha(rows.filter((m) => m.posicao == null))]
}

export const TIPO_LABEL: Record<TipoMovimento, string> = {
  ENTRADA_IMPORTACAO: 'Entrada (importação)',
  SAIDA_VENDA: 'Saída (venda)',
  SAIDA_ALOCACAO: 'Saída (alocação)',
  AJUSTE_ENTRADA: 'Ajuste (+)',
  AJUSTE_SAIDA: 'Ajuste (−)',
  ESTORNO_VENDA: 'Estorno de venda',
  ESTORNO_ALOCACAO: 'Estorno de alocação',
  ESTORNO_IMPORTACAO: 'Estorno de importação',
}

function Cartao(props: { rotulo: string; valor: string | number; aviso?: boolean }) {
  return (
    <div class="rounded-xl border border-edge bg-panel p-4">
      <div class="text-[11px] uppercase tracking-wide text-ink-subtle">{props.rotulo}</div>
      <div class="mt-1 text-2xl font-semibold tabular-nums" classList={{ 'text-warning': props.aviso, 'text-ink': !props.aviso }}>
        {props.valor}
      </div>
    </div>
  )
}

export function Estoque(props: { podeEditar: boolean }) {
  const [aba, setAba] = createSignal<'saldos' | 'kardex'>('saldos')
  const [ajuste, setAjuste] = createSignal<number | 'novo' | null>(null)
  const [abertos, setAbertos] = createSignal<Set<number>>(new Set())
  const [recarga, recarregar] = criarRecarga()
  const chave = () => ({ v: versaoDados(), r: recarga() })

  const [resumo] = createResource(chave, () => api.estoque.resumo())

  // ---- saldos
  const [busca, setBusca] = createSignal('')
  const [abaixoMin, setAbaixoMin] = createSignal(false)
  const [comSaldo, setComSaldo] = createSignal(true)
  const [linha, setLinha] = createSignal<number | ''>('')
  const [produto, setProduto] = createSignal<number | ''>('')
  const [linhas] = createResource(() => api.catalogo.linhas())
  const [produtos] = createResource(() => api.catalogo.produtos())
  const produtosDaLinha = () => (produtos() ?? []).filter((p) => !linha() || p.linhaId === linha())
  const [saldos] = createResource(
    () => ({ ...chave(), b: busca(), a: abaixoMin(), l: linha(), p: produto() }),
    (k) => api.estoque.saldos({ busca: k.b, abaixoMinimo: k.a, linhaId: k.l || undefined, produtoId: k.p || undefined }),
  )
  // "Só com saldo" ainda mostra modelos com PRÉ-VENDA (saldo 0, mas comprometido).
  const visiveis = createMemo(() => ordenarSaldos((saldos.latest ?? []).filter((m) => (comSaldo() ? m.saldoAtual !== 0 || m.preVendaTotal > 0 : true))))
  const podeArrastar = () => props.podeEditar && !busca().trim() && !linha() && !produto() && !abaixoMin()
  let arrastando: number | null = null
  const [sobre, setSobre] = createSignal<number | null>(null)
  const [erroOrdem, setErroOrdem] = createSignal<string | null>(null)
  const soltar = async (destino: number) => {
    const de = arrastando
    arrastando = null
    setSobre(null)
    if (de === null || de === destino || !podeArrastar()) return
    // ordem GLOBAL (inclui os ocultos por "só com saldo"): move o arrastado para antes do destino
    const vis = visiveis().map((m) => m.id)
    const movido = vis[de]
    const alvo = vis[destino]
    const ids = ordenarSaldos(saldos.latest ?? []).map((m) => m.id).filter((id) => id !== movido)
    const pos = ids.indexOf(alvo)
    ids.splice(pos < 0 ? ids.length : pos, 0, movido)
    try {
      setErroOrdem(null)
      await api.estoque.reordenarSaldos(ids)
    } catch (e) {
      setErroOrdem(mensagemErro(e))
    }
    recarregar()
  }
  const alternar = (id: number) => {
    const n = new Set(abertos())
    n.has(id) ? n.delete(id) : n.add(id)
    setAbertos(n)
  }

  // ---- kardex
  const [kTipo, setKTipo] = createSignal<TipoMovimento | ''>('')
  const [kDe, setKDe] = createSignal('')
  const [kAte, setKAte] = createSignal('')
  const [kBusca, setKBusca] = createSignal('')
  const [kardex] = createResource(
    () => (aba() === 'kardex' ? { ...chave(), t: kTipo(), de: kDe(), ate: kAte() } : false),
    (k) => api.estoque.kardex({ tipo: k.t || undefined, dataDe: k.de || undefined, dataAte: k.ate || undefined }),
  )
  const kardexVisivel = createMemo(() => {
    const t = kBusca().trim().toLowerCase()
    const l = kardex.latest ?? []
    return t ? l.filter((m) => m.codigoModelo.toLowerCase().includes(t) || (m.observacao ?? '').toLowerCase().includes(t)) : l
  })

  return (
    <div class="flex flex-col gap-5 p-4 sm:p-6">
      <div class="flex flex-wrap items-center gap-3">
        <div class="mr-auto">
          <h1 class="text-xl font-semibold text-ink">Estoque</h1>
          <p class="text-xs text-ink-subtle">Saldos derivados do kardex (nunca editados diretamente).</p>
        </div>
        <Show when={props.podeEditar}>
          <Button variant="accent" size="sm" onClick={() => setAjuste('novo')}>Ajuste de inventário</Button>
        </Show>
      </div>

      <Show when={resumo.latest}>
        {(r) => (
          <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Cartao rotulo="Modelos com saldo" valor={r().modelosComSaldo} />
            <Cartao rotulo="Abaixo do mínimo" valor={r().abaixoMinimo} aviso />
            <Cartao rotulo="Valor a preço de venda" valor={brl(r().valorVendaBRL)} />
            <Cartao rotulo="Valor a custo (landed)" valor={brl(r().valorCustoBRL)} />
          </div>
        )}
      </Show>

      <Tabs aria-label="Vista" size="sm" value={aba()} onChange={setAba} options={[{ value: 'saldos', label: 'Saldos' }, { value: 'kardex', label: 'Kardex (extrato)' }]} />

      <Show when={aba() === 'saldos'}>
        <div class="flex flex-wrap items-center gap-2">
          <Input class="w-64 max-w-full" placeholder="Buscar modelo…" value={busca()} onInput={(e) => setBusca(e.currentTarget.value)} />
          <Selecao class="w-48" value={linha()} onChange={(e) => (setLinha(e.currentTarget.value ? Number(e.currentTarget.value) : ''), setProduto(''))} aria-label="Linha">
            <option value="">Todas as linhas</option>
            <For each={linhas() ?? []}>{(l) => <option value={l.id}>{l.nome}</option>}</For>
          </Selecao>
          <Selecao class="w-56" value={produto()} onChange={(e) => setProduto(e.currentTarget.value ? Number(e.currentTarget.value) : '')} aria-label="Produto">
            <option value="">Todos os produtos</option>
            <For each={produtosDaLinha()}>{(p) => <option value={p.id}>{p.nomeComercial}</option>}</For>
          </Selecao>
          <Marcar checked={comSaldo()} onChange={setComSaldo}>Só com saldo</Marcar>
          <Marcar checked={abaixoMin()} onChange={setAbaixoMin}>Abaixo do mínimo</Marcar>
          <Button
            variant="secondary"
            size="sm"
            class="ml-auto"
            disabled={!(saldos.latest ?? []).some((m) => m.saldoAtual > 0)}
            title="Gera um PDF só com os itens em estoque (saldo > 0), respeitando os filtros atuais. Sem preço e sem a importação de origem."
            onClick={() => gerarExtratoEstoquePDF(ordenarSaldos(saldos.latest ?? []))}
          >
            Extrato em PDF
          </Button>
        </div>
        <p class="text-xs text-ink-placeholder -mt-2">
          {podeArrastar()
            ? 'Arraste pela alça ⠿ para reordenar — a ordem é salva automaticamente.'
            : 'Para reordenar arrastando, limpe a busca e os filtros (linha/produto/abaixo do mínimo).'}
        </p>
        <Erro mensagem={erroOrdem()} />
        <Show
          when={visiveis().length > 0}
          fallback={<Vazio titulo={saldos.loading ? 'Carregando…' : 'Nada no estoque para esta visão'} texto="Lance um ajuste de inventário ou ajuste os filtros." />}
        >
          <div class={T.caixa}>
            <table class={T.tabela}>
              <thead class={T.cab}>
                <tr>
                  <th class={T.th}>Modelo</th>
                  <th class={`${T.th} text-right`}>Saldo</th>
                  <th class={`${T.th} text-right`}>Mín.</th>
                  <th class={`${T.th} text-right hidden md:table-cell`}>Preço</th>
                  <th class={`${T.th} text-right hidden md:table-cell`}>Valor</th>
                  <th class={T.th} />
                </tr>
              </thead>
              <tbody>
                <For each={visiveis()}>
                  {(m, idx) => {
                    const baixo = () => m.estoqueMinimo > 0 && m.saldoAtual < m.estoqueMinimo
                    const expande = () => m.saldoAtual >= 1 || m.preVendaTotal > 0
                    const aberto = () => abertos().has(m.id)
                    return (
                      <>
                        <tr
                          class={T.tr}
                          classList={{ 'cursor-pointer': expande(), 'bg-hover': aberto(), 'bg-accent/10': sobre() === idx() && arrastando !== null }}
                          onClick={() => expande() && alternar(m.id)}
                          title={expande() ? 'Ver de quais importações vem o estoque' : undefined}
                          onDragOver={(e) => {
                            if (arrastando === null) return
                            e.preventDefault()
                            setSobre(idx())
                          }}
                          onDrop={() => void soltar(idx())}
                        >
                          <td class={T.td}>
                            <Show when={podeArrastar()}>
                              <span
                                draggable="true"
                                class="mr-2 cursor-grab select-none text-ink-placeholder hover:text-ink"
                                title="Arrastar para reordenar"
                                onDragStart={(e) => {
                                  e.stopPropagation()
                                  arrastando = idx()
                                  e.dataTransfer?.setData('text/plain', String(m.id))
                                }}
                                onDragEnd={() => ((arrastando = null), setSobre(null))}
                                onClick={(e) => e.stopPropagation()}
                              >
                                ⠿
                              </span>
                            </Show>
                            <Show when={expande()}>
                              <span class="mr-2 inline-block text-[10px] text-accent transition-transform" classList={{ 'rotate-90': aberto() }}>▸</span>
                            </Show>
                            <span class="font-medium text-ink">{m.codigoModelo}</span>
                            <Show when={m.serializado === 1}><span class="ml-2"><Etiqueta tom="aviso">série</Etiqueta></span></Show>
                            <Show when={m.saldoAtual === 0 && m.preVendaTotal > 0}>
                              <span class="ml-2"><Etiqueta tom="aviso">pré-venda {num(m.preVendaTotal)}</Etiqueta></span>
                            </Show>
                          </td>
                          <td class={`${T.td} text-right tabular-nums font-semibold`} classList={{ 'text-warning': baixo(), 'text-ink': !baixo() }}>{num(m.saldoAtual)}</td>
                          <td class={`${T.td} text-right tabular-nums text-ink-subtle`}>{m.estoqueMinimo || '—'}</td>
                          <td class={`${T.td} text-right tabular-nums text-ink-muted hidden md:table-cell`}>{brl(m.precoVendaPadrao)}</td>
                          <td class={`${T.td} text-right tabular-nums text-ink hidden md:table-cell`}>{m.precoVendaPadrao ? brl(m.saldoAtual * m.precoVendaPadrao) : '—'}</td>
                          <td class={`${T.td} text-right`}>
                            <Show when={props.podeEditar}>
                              <Button variant="ghost" size="sm" onClick={(e: MouseEvent) => (e.stopPropagation(), setAjuste(m.id))}>Ajustar</Button>
                            </Show>
                          </td>
                        </tr>
                        <Show when={aberto() && expande()}>
                          <tr class="border-b border-edge-muted bg-page/40">
                            <td colSpan={6} class="p-0">
                              <OrigemEstoque modeloId={m.id} recarga={recarga()} />
                            </td>
                          </tr>
                        </Show>
                      </>
                    )
                  }}
                </For>
              </tbody>
            </table>
          </div>
        </Show>
      </Show>

      <Show when={aba() === 'kardex'}>
        <div class="flex flex-wrap items-center gap-2">
          <Input class="w-64 max-w-full" placeholder="Buscar modelo/observação…" value={kBusca()} onInput={(e) => setKBusca(e.currentTarget.value)} />
          <Selecao class="w-56" value={kTipo()} onChange={(e) => setKTipo(e.currentTarget.value as TipoMovimento | '')} aria-label="Tipo">
            <option value="">Todos os tipos</option>
            <For each={Object.keys(TIPO_LABEL) as TipoMovimento[]}>{(t) => <option value={t}>{TIPO_LABEL[t]}</option>}</For>
          </Selecao>
          <Input type="date" class="w-40" value={kDe()} onInput={(e) => setKDe(e.currentTarget.value)} aria-label="De" />
          <Input type="date" class="w-40" value={kAte()} onInput={(e) => setKAte(e.currentTarget.value)} aria-label="Até" />
        </div>
        <Show when={kardexVisivel().length > 0} fallback={<Vazio titulo={kardex.loading ? 'Carregando…' : 'Sem movimentos'} texto="Nenhum movimento de estoque no filtro atual." />}>
          <div class={T.caixa}>
            <table class={T.tabela}>
              <thead class={T.cab}>
                <tr>
                  <th class={T.th}>Data</th>
                  <th class={T.th}>Modelo</th>
                  <th class={T.th}>Tipo</th>
                  <th class={`${T.th} text-right`}>Qtd</th>
                  <th class={`${T.th} hidden md:table-cell`}>Série</th>
                  <th class={T.th}>Cliente</th>
                  <th class={`${T.th} hidden lg:table-cell`}>Usuário</th>
                  <th class={`${T.th} hidden lg:table-cell`}>Observação</th>
                </tr>
              </thead>
              <tbody>
                <For each={kardexVisivel()}>
                  {(m) => (
                    <tr class={T.tr}>
                      <td class={`${T.td} text-ink-muted whitespace-nowrap`}>{m.dataMovimento?.slice(0, 16).replace('T', ' ')}</td>
                      <td class={`${T.td} text-ink`}>{m.codigoModelo}</td>
                      <td class={T.td}><Etiqueta tom={m.quantidade >= 0 ? 'sucesso' : 'aviso'}>{TIPO_LABEL[m.tipoMovimento]}</Etiqueta></td>
                      <td class={`${T.td} text-right tabular-nums font-medium`} classList={{ 'text-success': m.quantidade >= 0, 'text-warning': m.quantidade < 0 }}>
                        {m.quantidade > 0 ? `+${m.quantidade}` : m.quantidade}
                      </td>
                      <td class={`${T.td} text-ink-subtle hidden md:table-cell`}>{m.numeroSerie || '—'}</td>
                      <td class={`${T.td} text-ink-muted`}>{m.clienteNome || '—'}</td>
                      <td class={`${T.td} text-ink-subtle hidden lg:table-cell`}>{m.usuarioNome || '—'}</td>
                      <td class={`${T.td} text-ink-subtle hidden lg:table-cell`}>{m.observacao || '—'}</td>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </div>
        </Show>
      </Show>

      <Show when={ajuste() !== null}>
        <AjusteEstoque modeloInicial={ajuste() === 'novo' ? undefined : (ajuste() as number)} aoFechar={() => setAjuste(null)} aoSalvar={recarregar} />
      </Show>
    </div>
  )
}

/** Restante REAL de cada importação (entrou − alocado), com o custo landed. A soma bate com o saldo. */
function OrigemEstoque(props: { modeloId: number; recarga: number }) {
  const [d] = createResource(() => ({ id: props.modeloId, v: versaoDados(), r: props.recarga }), ({ id }) => api.estoque.origemEstoque(id))
  return (
    <div class="px-4 py-3">
      <Show when={(d.latest ?? []).length > 0} fallback={<p class="text-xs text-ink-subtle">{d.loading ? 'Carregando…' : 'Sem estoque para detalhar.'}</p>}>
        <div class="mb-1.5 text-[11px] uppercase tracking-wide text-ink-subtle">Restante por importação (o que entrou − o que foi alocado em cada uma)</div>
        <table class="w-full text-sm">
          <thead class="text-left text-[11px] text-ink-subtle">
            <tr>
              <th class="py-1 pr-3 font-medium">Importação</th>
              <th class="py-1 pr-3 font-medium">Recebido</th>
              <th class="py-1 pr-3 font-medium text-right">Qtd em estoque</th>
              <th class="py-1 pr-3 font-medium text-right">Custo landed (un.)</th>
              <th class="py-1 pr-3 font-medium text-right">Custo FOB (un.)</th>
            </tr>
          </thead>
          <tbody>
            <For each={d.latest}>
              {(o) => (
                <tr class="border-t border-edge-muted" classList={{ 'text-warning': o.preVenda }}>
                  <td class="py-1 pr-3 font-medium text-ink">
                    {o.numeroInvoice}
                    <Show when={o.preVenda}><span class="ml-1.5 text-[10px] text-warning">pré-venda</span></Show>
                  </td>
                  <td class="py-1 pr-3 text-ink-muted">{o.preVenda ? <span class="text-warning">não recebida</span> : (o.dataRecebimento?.slice(0, 10) ?? '—')}</td>
                  <td class="py-1 pr-3 text-right tabular-nums font-semibold" classList={{ 'text-warning': o.preVenda, 'text-ink': !o.preVenda }}>
                    {o.preVenda ? `−${num(o.quantidade)}*` : num(o.quantidade)}
                  </td>
                  <td class="py-1 pr-3 text-right tabular-nums text-success">{o.custoLandedBrl != null ? brl(o.custoLandedBrl) : '—'}</td>
                  <td class="py-1 pr-3 text-right tabular-nums text-ink-muted">{o.custoUnitarioOrigem ? `${num(o.custoUnitarioOrigem)} ${o.moeda}` : '—'}</td>
                </tr>
              )}
            </For>
          </tbody>
        </table>
        <Show when={(d.latest ?? []).some((o) => o.preVenda)}>
          <p class="mt-2 text-[11px] text-warning">
            * Pré-venda: já vendido de uma importação ainda <b>não recebida</b>. Não baixa o estoque agora — a baixa acontece quando a importação for recebida.
          </p>
        </Show>
      </Show>
    </div>
  )
}

/** Escolher um modelo pelo código (busca local, como no app atual). */
export function BuscaModelo(props: { aoEscolher: (m: ModeloLista) => void; placeholder?: string; excluirIds?: number[] }) {
  const [t, setT] = createSignal('')
  const [modelos] = createResource(() => api.catalogo.modelos({ incluirInativos: false }))
  const achados = () => {
    const q = t().trim().toLowerCase()
    if (!q) return []
    return (modelos() ?? []).filter((m) => m.codigoModelo.toLowerCase().includes(q) && !props.excluirIds?.includes(m.id)).slice(0, 8)
  }
  return (
    <div class="relative">
      <Input placeholder={props.placeholder ?? 'Buscar código do modelo…'} value={t()} onInput={(e) => setT(e.currentTarget.value)} />
      <Show when={achados().length > 0}>
        <div class="absolute z-20 mt-1 w-full max-w-md overflow-hidden rounded-md border border-edge bg-panel shadow-lg">
          <For each={achados()}>
            {(m) => (
              <button type="button" class="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm text-ink hover:bg-hover" onClick={() => (props.aoEscolher(m), setT(''))}>
                <span>
                  {m.codigoModelo}
                  <Show when={m.serializado === 1}><span class="ml-2"><Etiqueta tom="aviso">série</Etiqueta></span></Show>
                </span>
                <span class="text-xs text-ink-subtle">saldo {num(m.saldoAtual)}</span>
              </button>
            )}
          </For>
        </div>
      </Show>
    </div>
  )
}

function AjusteEstoque(props: { modeloInicial?: number; aoFechar: () => void; aoSalvar: () => void }) {
  const [modelo, setModelo] = createSignal<ModeloLista | null>(null)
  const [tipo, setTipo] = createSignal<'ENTRADA' | 'SAIDA'>('ENTRADA')
  const [qtd, setQtd] = createSignal(1)
  const [obs, setObs] = createSignal('')
  const [series, setSeries] = createSignal<string[]>([''])
  const [selecionadas, setSelecionadas] = createSignal<Set<number>>(new Set())
  const [erro, setErro] = createSignal<string | null>(null)
  const [salvando, setSalvando] = createSignal(false)
  const serializado = () => modelo()?.serializado === 1

  const [inicial] = createResource(() => props.modeloInicial ?? false, async (id) => (await api.catalogo.modelos({ incluirInativos: false })).find((m) => m.id === id) ?? null)
  createEffect(() => {
    const m = inicial()
    if (m) setModelo(m)
  })
  // unidades em estoque ao escolher SAÍDA de um modelo serializado
  const [unidades] = createResource(
    () => (modelo() && serializado() && tipo() === 'SAIDA' ? modelo()!.id : false),
    (id) => api.estoque.unidades(id, true),
  )
  createEffect(on(() => [modelo()?.id, tipo()], () => setSelecionadas(new Set<number>())))
  const quantidadeEfetiva = () => (serializado() && tipo() === 'SAIDA' ? selecionadas().size : qtd())
  const mudarQtd = (v: number) => {
    const q = Math.max(1, Math.floor(v || 1))
    setQtd(q)
    setSeries(Array.from({ length: q }, (_, i) => series()[i] ?? ''))
  }
  const alternar = (id: number) => {
    const n = new Set(selecionadas())
    n.has(id) ? n.delete(id) : n.add(id)
    setSelecionadas(n)
  }
  const salvar = async () => {
    setErro(null)
    const m = modelo()
    if (!m) return setErro('Selecione um modelo.')
    if (!obs().trim()) return setErro('A observação é obrigatória.')
    if (quantidadeEfetiva() < 1) return setErro('Informe a quantidade (ou selecione unidades).')
    setSalvando(true)
    try {
      await api.estoque.ajustar({
        modeloId: m.id,
        tipo: tipo(),
        quantidade: quantidadeEfetiva(),
        observacao: obs().trim(),
        series: serializado() && tipo() === 'ENTRADA' ? series() : undefined,
        unidadeIds: serializado() && tipo() === 'SAIDA' ? [...selecionadas()] : undefined,
      })
      props.aoSalvar()
      props.aoFechar()
    } catch (x) {
      setErro(mensagemErro(x))
    } finally {
      setSalvando(false)
    }
  }
  return (
    <Formulario
      aberto
      titulo="Ajuste de inventário"
      largura={640}
      aoFechar={props.aoFechar}
      aoEnviar={() => void salvar()}
      rodape={
        <>
          <span class="mr-auto self-center text-sm text-ink-muted">
            Movimento:{' '}
            <b classList={{ 'text-success': tipo() === 'ENTRADA', 'text-warning': tipo() === 'SAIDA' }}>
              {tipo() === 'ENTRADA' ? '+' : '−'}
              {quantidadeEfetiva()}
            </b>
          </span>
          <Button type="button" variant="ghost" onClick={() => props.aoFechar()}>Cancelar</Button>
          <Button type="submit" variant="accent" disabled={salvando() || !modelo()}>{salvando() ? 'Lançando…' : 'Lançar ajuste'}</Button>
        </>
      }
    >
      <Erro mensagem={erro()} />
      <Show
        when={modelo()}
        fallback={
          <Campo rotulo="Modelo" dica="Busque pelo código do modelo">
            <BuscaModelo aoEscolher={setModelo} placeholder="Ex.: VENT PRO F 600x600, GatEx DN 100…" />
          </Campo>
        }
      >
        <div class="flex items-center justify-between rounded-md border border-edge px-3 py-2">
          <div class="text-sm">
            <span class="font-medium text-ink">{modelo()!.codigoModelo}</span>
            <Show when={serializado()}><span class="ml-2"><Etiqueta tom="aviso">serializado</Etiqueta></span></Show>
            <span class="ml-2 text-ink-subtle">saldo atual {num(modelo()!.saldoAtual)}</span>
          </div>
          <Button type="button" variant="ghost" size="sm" onClick={() => setModelo(null)}>trocar</Button>
        </div>
        <div class="grid grid-cols-2 gap-3">
          <Campo rotulo="Tipo de ajuste">
            <Selecao value={tipo()} onChange={(e) => setTipo(e.currentTarget.value as 'ENTRADA' | 'SAIDA')}>
              <option value="ENTRADA">Entrada (+) — encontrado a mais</option>
              <option value="SAIDA">Saída (−) — perda / avaria / contagem</option>
            </Selecao>
          </Campo>
          <Show when={!(serializado() && tipo() === 'SAIDA')}>
            <Campo rotulo="Quantidade">
              <Input type="number" min={1} value={qtd()} onInput={(e) => mudarQtd(Number(e.currentTarget.value))} />
            </Campo>
          </Show>
        </div>
        <Show when={serializado() && tipo() === 'ENTRADA'}>
          <Campo rotulo="Números de série (opcional — em branco = pendente)">
            <div class="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <Index each={series().slice(0, 60)}>
                {(s, i) => <Input placeholder={`Unidade ${i + 1}`} value={s()} onInput={(e) => setSeries(series().map((x, j) => (j === i ? e.currentTarget.value : x)))} />}
              </Index>
            </div>
          </Campo>
        </Show>
        <Show when={serializado() && tipo() === 'SAIDA'}>
          <Campo rotulo={`Unidades em estoque — selecione as que saem (${selecionadas().size} selecionada(s))`}>
            <Show when={(unidades() ?? []).length > 0} fallback={<p class="text-sm text-ink-subtle">{unidades.loading ? 'Carregando…' : 'Nenhuma unidade em estoque para este modelo.'}</p>}>
              <div class="max-h-52 overflow-auto rounded-md border border-edge">
                <For each={unidades()}>
                  {(u: UnidadeEstoqueDTO) => (
                    <label class="flex cursor-pointer items-center gap-2 border-b border-edge-muted px-3 py-1.5 text-sm last:border-0" classList={{ 'bg-accent/10': selecionadas().has(u.id) }}>
                      <input type="checkbox" checked={selecionadas().has(u.id)} onChange={() => alternar(u.id)} />
                      <span class="text-ink">#{u.id}</span>
                      <span class="text-ink-muted">{u.numeroSerie || '(série pendente)'}</span>
                    </label>
                  )}
                </For>
              </div>
            </Show>
          </Campo>
        </Show>
        <Campo rotulo="Observação (obrigatória)">
          <AreaTexto value={obs()} onInput={(e) => setObs(e.currentTarget.value)} placeholder="Motivo do ajuste (inventário físico, avaria, perda…)" />
        </Campo>
      </Show>
    </Formulario>
  )
}
